import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, History, Plus, Trash2, X } from "lucide-react";
import { sendChat, listSessions, getSession, deleteSession } from "../../lib/api";

const SUGGESTED = [
  "Summarize the strongest unresolved cases.",
  "Which sensors capture the most UAP?",
  "Are there geographic clusters in the data?",
  "What is the Tic Tac incident?",
];

const newSessionId = () =>
  `pursue-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export default function AIChat({ activeCase }) {
  const [sessionId, setSessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "PURSUE Analyst online. Ask me about any case in the database, or use the suggestions below.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const loadSessionsList = async () => {
    setLoadingSessions(true);
    try {
      setSessions(await listSessions());
    } finally {
      setLoadingSessions(false);
    }
  };

  useEffect(() => {
    if (historyOpen) loadSessionsList();
  }, [historyOpen]);

  const openSession = async (id) => {
    const data = await getSession(id);
    const expanded = [];
    data.messages.forEach((m) => {
      expanded.push({ role: "user", text: m.user_message });
      expanded.push({ role: "assistant", text: m.ai_answer });
    });
    if (expanded.length === 0) {
      expanded.push({
        role: "assistant",
        text: "Session is empty.",
      });
    }
    setSessionId(id);
    setMessages(expanded);
    setHistoryOpen(false);
  };

  const removeSession = async (id, e) => {
    e.stopPropagation();
    await deleteSession(id);
    setSessions((s) => s.filter((x) => x.session_id !== id));
  };

  const newChat = () => {
    setSessionId(newSessionId());
    setMessages([
      {
        role: "assistant",
        text:
          "PURSUE Analyst online. Ask me about any case in the database, or use the suggestions below.",
      },
    ]);
    setHistoryOpen(false);
  };

  const submit = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const res = await sendChat({
        session_id: sessionId,
        message: q,
        case_id: activeCase?.case_id ?? null,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.answer }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "⚠ Analyst signal lost. Please retry." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="ai-chat"
      className="border border-zinc-800 bg-zinc-950 flex h-[420px] relative overflow-hidden"
    >
      {/* History sidebar */}
      {historyOpen && (
        <div
          data-testid="chat-history-sidebar"
          className="absolute inset-y-0 left-0 w-[55%] bg-zinc-950 border-r border-zinc-800 z-10 flex flex-col"
        >
          <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-300 flex items-center gap-1.5">
              <History className="h-3 w-3 text-amber-400" /> Session Log
            </span>
            <button
              data-testid="close-history-btn"
              onClick={() => setHistoryOpen(false)}
              className="text-zinc-500 hover:text-zinc-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            {loadingSessions && (
              <div className="px-3 py-3 font-mono text-[10px] text-zinc-500">Loading…</div>
            )}
            {!loadingSessions && sessions.length === 0 && (
              <div className="px-3 py-3 font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
                No prior sessions
              </div>
            )}
            {sessions.map((s) => (
              <button
                key={s.session_id}
                data-testid={`session-row-${s.session_id}`}
                onClick={() => openSession(s.session_id)}
                className="w-full text-left border-b border-zinc-900 px-3 py-2 hover:bg-zinc-900/60 transition-colors group"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] text-zinc-500 truncate">
                    {new Date(s.last_timestamp).toLocaleString()} · {s.count} msg
                  </span>
                  <Trash2
                    data-testid={`delete-session-${s.session_id}`}
                    onClick={(e) => removeSession(s.session_id, e)}
                    className="h-3 w-3 text-zinc-700 hover:text-red-400 opacity-40 group-hover:opacity-100 transition-opacity shrink-0"
                  />
                </div>
                <div className="text-xs text-zinc-300 mt-0.5 line-clamp-2">
                  {s.last_message}
                </div>
                {s.last_case_id && (
                  <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400/80 mt-0.5">
                    {s.last_case_id}
                  </div>
                )}
              </button>
            ))}
          </div>
          <button
            data-testid="new-chat-btn"
            onClick={newChat}
            className="border-t border-zinc-800 px-3 py-2 flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:text-amber-400 hover:bg-zinc-900"
          >
            <Plus className="h-3 w-3" /> New session
          </button>
        </div>
      )}

      {/* Main chat */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="border-b border-zinc-800 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse-amber shrink-0" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-300 truncate">
              PURSUE Analyst · Claude Sonnet 4.5
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {activeCase && (
              <span className="font-mono text-[10px] uppercase tracking-widest text-amber-400">
                CTX: {activeCase.case_id}
              </span>
            )}
            <button
              data-testid="toggle-history-btn"
              onClick={() => setHistoryOpen((v) => !v)}
              className="text-zinc-500 hover:text-amber-400 transition-colors"
              title="Session history"
            >
              <History className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="new-chat-toolbar-btn"
              onClick={newChat}
              className="text-zinc-500 hover:text-amber-400 transition-colors"
              title="New session"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-3 space-y-3">
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`chat-msg-${i}`}
              className={`text-sm leading-relaxed ${
                m.role === "user" ? "text-zinc-100" : "text-zinc-300"
              }`}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                {m.role === "user" ? "▸ OPERATOR" : "◆ ANALYST"}
              </div>
              <div
                className={`whitespace-pre-wrap font-sans ${
                  m.role === "assistant" ? "border-l-2 border-amber-400/60 pl-3" : "pl-3"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 font-mono text-xs text-zinc-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />
              Querying intelligence corpus…
            </div>
          )}
        </div>

        {messages.length <= 1 && !loading && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {SUGGESTED.map((s, i) => (
              <button
                key={i}
                data-testid={`chat-suggest-${i}`}
                onClick={() => submit(s)}
                className="border border-zinc-800 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-amber-400 hover:border-amber-400/60 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="border-t border-zinc-800 p-2 flex gap-2"
        >
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              activeCase ? `Ask about ${activeCase.case_id}…` : "Ask the analyst…"
            }
            className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2 font-sans text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-amber-400 focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            data-testid="chat-send-btn"
            className="bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 px-3 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-1.5"
          >
            <Send className="h-3.5 w-3.5" />
            SEND
          </button>
        </form>
      </div>
    </div>
  );
}
